import React, { useEffect, useState } from "react";

import {
  gatherActiveFilters,
  onChangeQuickFilter,
  onColumnVisibilityChanged,
  onFilterChange,
  resetAllActiveFilters,
} from "@src/features/table_filtering/utils/base_state_handler_utils";
import {
  clearRequestRepliesCustomTableFilterProperty,
  clearRequestRepliesFilter,
} from "@src/services/resourcePlanSlice";
import { useAppDispatch, useAppSelector } from "@src/setupStore";
import { ActiveFilter } from "@src/types";
import { ColDef, GridApi } from "ag-grid-community";

interface UseRequestRepliesStateChangeHandlerProps {
  gridApi: React.MutableRefObject<GridApi>;
  columnDefs: ColDef[];
  filterCallBack: () => void;
}

interface UseRequestRepliesStateChangeHandlerResponse {
  activeFilters: ActiveFilter[];
  resetActiveFilters: (key?: string, value?: string) => void;
  setTableRendered: () => void;
}

export const useRequestRepliesStateChangeHandler = ({
  gridApi,
  columnDefs,
  filterCallBack,
}: UseRequestRepliesStateChangeHandlerProps): UseRequestRepliesStateChangeHandlerResponse => {
  const [isTableRendered, setIsTableRendered] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const dispatch = useAppDispatch();

  const {
    requestRepliesTableState: { quickFilter, customTableFilters, columns },
  } = useAppSelector((state) => state.resourcePlanSlice);

  /**
   * Load filter state from store to grid
   */
  useEffect(() => {
    if (!gridApi.current && !isTableRendered) return;

    onChangeQuickFilter(quickFilter, gridApi.current, filterCallBack);
    onColumnVisibilityChanged(columns, gridApi.current, columnDefs);

    for (const key of Object.keys(customTableFilters)) {
      const values: string[] | boolean = customTableFilters[key];
      if (Array.isArray(values)) {
        onFilterChange(values, key, gridApi.current, filterCallBack);
      }
    }

    const activeFilters = gatherActiveFilters(customTableFilters);
    setActiveFilters(activeFilters);
  }, [columns, quickFilter, customTableFilters, gridApi.current, isTableRendered]);

  function resetActiveFilterByKey(key: string, value: string): void {
    const valueToBeCleared = { key, value };
    dispatch(clearRequestRepliesCustomTableFilterProperty(valueToBeCleared));
  }

  function resetActiveFilters(key?: string, value?: string): void {
    if (key !== undefined && value !== undefined) {
      resetActiveFilterByKey(key, value);
    } else {
      resetAllActiveFilters(gridApi.current);
      dispatch(clearRequestRepliesFilter());
    }

    filterCallBack();
  }

  return {
    activeFilters,
    resetActiveFilters,
    setTableRendered: () => setIsTableRendered(true),
  };
};
